import { CellClassParams, ColDef, ColGroupDef } from 'ag-grid-community';
import { DataTypes as DT } from 'business/definitions';
import { formatDisplay } from 'helpers/formatHelper';
import { CellStyleT } from 'ui/AgGridReact/colDefConfig';
import {
    ApT,
    AMOUNT_ARBITRATED_YEAR_PREFIX,
    AMOUNT_ESTIMATED_YEAR_PREFIX,
    AMOUNT_FIELD,
    AMOUNT_REALIZED_YEAR_PREFIX,
    AMOUNT_REQUESTED_YEAR_PREFIX,
    AMOUNT_SUPP_YEAR_PREFIX,
    AMOUNT_VOTED_YEAR_PREFIX,
    NAME_FIELD,
    PERCENT_REALIZED_FIELD,
    PERCENT_REALIZED_YEAR_PREFIX,
    REALIZED_FIELD,
    FULL_AMOUNT_YEAR_PREFIX,
} from './types';
import { TFunction } from 'i18next';

export const cellStyleBold = { fontWeight: 600, justifyContent: 'flex-end' };
export const cellStyleSemiBold = { fontWeight: 510 };

const cellColor = (current: string, reference: string) => {
    if (parseFloat(current) !== parseFloat(reference))
        return { ...cellStyleBold, color: 'DarkOrange' };
    return { ...cellStyleBold, color: 'green' };
};

const DEFAULT_COL_DEFS = {
    resizable: true,
    editable: false,
};

const isColGroupDefT = (
    variableToCheck: unknown,
): variableToCheck is ColGroupDef =>
    (variableToCheck as ColGroupDef)?.children !== undefined;

export const applyDefaultColDefs = (coldefs: (ColDef | ColGroupDef)[]) => {
    const updatedColDefs: (ColDef | ColGroupDef)[] = coldefs.map((col) => {
        if (isColGroupDefT(col))
            return {
                ...col,
                ...DEFAULT_COL_DEFS,
                children: applyDefaultColDefs(col.children),
            } as ColGroupDef;
        return {
            ...col,
            ...DEFAULT_COL_DEFS,
        };
    });
    return updatedColDefs;
};

export const buildAmountColDef = <T extends Record<string, number>>(
    name: string,
    field: string,
    dataType: DataTypesT,
    cellStyle: CellStyleT | ((arg1: unknown) => CellStyleT),
    hidden: boolean = false,
    width: number = 120,
): ColDef => ({
    headerName: name,
    field,
    valueGetter: ({ data }: { data: T }) => data[field],
    valueFormatter: ({ data }: { data: T }) =>
        formatDisplay(
            // we don't want to round fraction values as they will be rounded to 0 or 1
            dataType !== DT.fraction && data[field]
                ? Math.round(data[field])
                : data[field],
            dataType,
        ),
    sortable: true,
    floatingFilter: false,
    filter: 'agNumberColumnFilter',
    floatingFilterComponent: 'numberFloatingFilter',
    cellStyle,
    type: 'numericColumn',
    hide: hidden,
    width,
});

const getDateApColumns = (
    currentYear: number,
    requestedYear: number,
    t: TranslationT,
): ColDef[] => {
    const columnsPerYear = [];

    const dateColWidth = 107;
    // function to compare with currentColumn with reference
    const cellStyler = (params: CellClassParams) =>
        cellColor(
            params.value,
            params.data[`${AMOUNT_ESTIMATED_YEAR_PREFIX}..${currentYear}`],
        );
    columnsPerYear.push(
        buildAmountColDef(
            t('viewAllAps.dateExpected'),
            `${AMOUNT_ESTIMATED_YEAR_PREFIX}..${currentYear}`,
            DT.money,
            cellStyleBold,
            false,
            dateColWidth,
        ),
    );
    // Requested year
    if (currentYear === requestedYear) {
        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.dateProposed'),
                `${AMOUNT_REQUESTED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyler,
                false,
                dateColWidth,
            ),
        );

        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.dateArbitrated'),
                `${AMOUNT_ARBITRATED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyler,
                true,
                dateColWidth,
            ),
        );

        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.dateVoted'),
                `${AMOUNT_VOTED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyler,
                true,
                dateColWidth,
            ),
        );

        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.dateSupp'),
                `${AMOUNT_SUPP_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                true,
                dateColWidth,
            ),
        );
    }
    // Past year
    if (currentYear <= requestedYear) {
        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.fullAmount'),
                `${FULL_AMOUNT_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                true,
                dateColWidth,
            ),
        );
        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.dateRealized'),
                `${AMOUNT_REALIZED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
        columnsPerYear.push(
            buildAmountColDef(
                t('viewAllAps.datePercentRealized'),
                `${PERCENT_REALIZED_YEAR_PREFIX}..${currentYear}`,
                DT.fraction,
                cellStyleBold,
                true,
                dateColWidth,
            ),
        );
    }
    return columnsPerYear;
};

export const getColumns = (
    dates: number[],
    t: TranslationT,
    isAP: boolean,
    targetYear: number,
): (ColDef | ColGroupDef)[] => {
    const summaryColWith = 120;
    const headers: (ColDef | ColGroupDef)[] = [
        {
            headerName: t('viewAllAps.summaryHeader', {
                ressource_type: isAP ? 'AP' : 'AE',
            }),
            children: [
                {
                    headerName: t('viewAllAps.ressourceLabel', {
                        ressource_type: isAP ? 'AP' : 'AE',
                    }),
                    field: NAME_FIELD,
                    filter: 'agTextColumnFilter',
                    sortable: true,
                    floatingFilter: true,
                    pinned: 'left',
                    width: 280,
                    cellRenderer: 'agGroupCellRenderer',
                    floatingFilterComponent: 'textFloatingFilter',
                    cellStyle: cellStyleSemiBold,
                    valueGetter: ({ data }: { data: ApT }) => data[NAME_FIELD],
                    valueFormatter: ({ data }: { data: ApT }) =>
                        formatDisplay(data[NAME_FIELD], DT.text),
                },
                buildAmountColDef(
                    t('viewAllAps.apAeAmount', {
                        ressource_type: isAP ? 'AP' : 'AE',
                    }),
                    AMOUNT_FIELD,
                    DT.money,
                    cellStyleBold,
                    false,
                    summaryColWith,
                ),
                buildAmountColDef(
                    t('viewAllAps.ressourceRealized'),
                    REALIZED_FIELD,
                    DT.money,
                    cellStyleBold,
                    true,
                    summaryColWith,
                ),
                buildAmountColDef(
                    t('viewAllAps.percentRealized'),
                    PERCENT_REALIZED_FIELD,
                    DT.fraction,
                    cellStyleBold,
                    false,
                    summaryColWith,
                ),
            ],
        },
    ];
    dates.forEach((date) =>
        headers.push({
            headerName: t('viewAllAps.dateHeader', {
                date,
            }),
            children: getDateApColumns(date, targetYear, t),
        }),
    );

    return applyDefaultColDefs(headers);
};

export const getSideBar = (t: TFunction) => ({
    toolPanels: [
        {
            id: 'filters',
            labelDefault: t('viewAllAps.filterPanel'),
            labelKey: t('viewAllAps.filterPanel'),
            iconKey: 'filter',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressColumnMove: true,
                suppressPivotMode: true,
                suppressColumnFilter: true,
                suppressValues: true,
                suppressRowGroups: true,
                suppressColumnSelectAll: true,
                suppressColumnExpandAll: true,
                contractColumnSelection: true,
            },
        },
    ],
});
